import * as React from 'react';
import {
  Chart,
  BarSeries,
  ValueAxis,
  LineSeries,
  ScatterSeries,
  ZoomAndPan,
  ArgumentAxis,
  Tooltip,
} from '@devexpress/dx-react-chart-material-ui';

import {
  ArgumentScale,
  EventTracker,
  SeriesRef,
  ValueScale,
  Viewport,
} from '@devexpress/dx-react-chart';
import { green, purple, red } from 'globals/colors';

import { ReactElement, useState } from 'react';
import {
  amountTickFormatterForValueAxis,
  getAmountTooltipContentComponent,
  getTopRoundBarSeriesPointComponent,
  scaleDateFactory,
} from 'globals/graph-utils';
import { getDateDiffInDays } from 'globals/utils';
import { useMediaQuery, useTheme } from '@material-ui/core';
import {
  FsTrendChartSingleRowDataProps,
  getInitialDayRange,
  getZoomLevel,
  modifyDomain,
} from './FsTrendChart.utils';
import {
  ValueAxisGridComponent,
  ValueAxisLabelComponent,
  ArgumentAxisLabelComponent,
  TooltipSheetComponent,
  TooltipOverlay,
} from './FsTrendChart.style-elements';

interface FsTrendChartDataProps {
  chartData: FsTrendChartSingleRowDataProps[];
}

const FsTrendChart: React.FC<FsTrendChartDataProps> = ({ chartData }) => {
  const [targetItem, setTargetItem] = useState<SeriesRef>();
  const initialDayRange = getInitialDayRange(chartData);
  const [viewport, setViewport] = useState<Viewport>({});
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  // TODO: i need to get such cashflow-summary from API
  const currency = 'USD';
  const format = 'en-US';

  const changeTargetItem = (target?: SeriesRef): void => {
    if (target && target?.series === 'scatterSeries') return;
    setTargetItem(target);
  };

  const changeViewport = (v: Viewport): void => {
    if (v.argumentStart && v.argumentEnd) {
      const newDayRange = getDateDiffInDays(v.argumentStart, v.argumentEnd);
      const zoomLevelValue = getZoomLevel(initialDayRange, newDayRange);
      if (zoomLevelValue < 20) {
        setZoomLevel(zoomLevelValue);
        setViewport(v);
      }
    }
  };

  const BarSeriesPointComponent = getTopRoundBarSeriesPointComponent({
    barWidth: zoomLevel > 2 ? zoomLevel * 2 : undefined,
    rx: zoomLevel > 2 ? 4 : 0,
    ry: zoomLevel > 2 ? 4 : 0,
  });
  const TooltipContentComponent = getAmountTooltipContentComponent({
    currency,
    format,
  });

  return (
    <Chart data={chartData} height={300}>
      {/* <p>ZOOM LEVEL: {zoomLevel}</p> */}

      {/* X */}
      <ArgumentScale factory={scaleDateFactory()} />
      <ArgumentAxis
        showLine={false}
        labelComponent={ArgumentAxisLabelComponent}
      />

      {/* Y */}
      <ValueScale name="valueScale" modifyDomain={modifyDomain} />
      <ValueAxis
        labelComponent={ValueAxisLabelComponent}
        scaleName="valueScale"
        gridComponent={ValueAxisGridComponent}
        tickFormat={amountTickFormatterForValueAxis({ format, currency })}
      />

      {/* Income */}
      <BarSeries
        name="income"
        valueField="income"
        argumentField="x"
        color={green.MAIN}
        scaleName="valueScale"
        pointComponent={BarSeriesPointComponent}
      />

      {/* Outgoing */}
      <BarSeries
        name="outgoing"
        valueField="outgoing"
        argumentField="x"
        color={red.MAIN}
        scaleName="valueScale"
        pointComponent={BarSeriesPointComponent}
      />
      <LineSeries
        name="balance"
        valueField="balance"
        argumentField="x"
        color={purple.BRAND}
        scaleName="valueScale"
      />

      {/* DOTS */}
      {/* Dots visible after certain zoom level */}
      {zoomLevel > 2 && (
        <ScatterSeries
          name="scatterSeries"
          valueField="balance"
          argumentField="x"
          color={purple.BRAND}
          scaleName="valueScale"
        />
      )}

      {/* EVENT TRACKER */}
      <EventTracker />

      {/* ZOOM AND PAN */}
      {isUpSm && (
        <ZoomAndPan viewport={viewport} onViewportChange={changeViewport} />
      )}

      {/* TOOLTIP */}
      <Tooltip
        targetItem={targetItem}
        onTargetItemChange={changeTargetItem}
        sheetComponent={TooltipSheetComponent}
        contentComponent={TooltipContentComponent}
        overlayComponent={(props): ReactElement<Tooltip.OverlayProps> => (
          <TooltipOverlay {...props} />
        )}
      />
    </Chart>
  );
};

export default FsTrendChart;
